import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { VerifyProvider } from "./context/VerifyContext";
import HomePage from "./pages/HomePage";
import SSOHandler from "./components/auth/SSOHandler";
import SmoothScroll from "./components/effects/SmoothScroll";
import ScrollToTop from "./components/effects/ScrollToTop";
import useActivityTracker from "./hooks/useActivityTracker";
// CRM Customer Activity + Website Journey tracker.
// Import for side-effects: auto-installs pushState/popstate/beforeunload listeners
// and starts a 30s batch flush timer. See src/lib/crmTracker.js.
import "./lib/crmTracker";

const ActivityTracker = () => { useActivityTracker(); return null; };
import GlobalModalScrollLock from "./components/effects/GlobalModalScrollLock";
import { SplashController } from "./components/effects/SplashLoader";
import MobileBottomNav from "./components/layout/MobileBottomNav";
import JsonLd from "./components/seo/JsonLd";
import Analytics from "./components/seo/Analytics";
import PromoPopup from "./components/promo/PromoPopup";
import ExitIntentPopup from "./components/promo/ExitIntentPopup";
import AIChatWidget from "./components/AIChatWidget";
import RouteScrollGuard from "./components/effects/RouteScrollGuard";
import ExternalTradeRedirect from "./components/effects/ExternalTradeRedirect";
import CookieConsentBanner from "./components/common/CookieConsentBanner";
import StatusPage from "./pages/StatusPage";
import { Toaster as SonnerToaster } from "sonner";
import { organizationSchema, financialServiceSchema, websiteSchema } from "./seo/schemas";

// Lazy load all pages - only loads when user navigates to them
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const MarketsPage = lazy(() => import("./pages/MarketsPage"));
const LearnPage = lazy(() => import("./pages/LearnPage"));
const CompanyPage = lazy(() => import("./pages/CompanyPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const FeesPage = lazy(() => import("./pages/FeesPage"));
const SpotTradingPage = lazy(() => import("./pages/SpotTradingPage"));
const FuturesPage = lazy(() => import("./pages/FuturesPage"));
const MarginTradingPage = lazy(() => import("./pages/MarginTradingPage"));
const CopyTradingPage = lazy(() => import("./pages/CopyTradingPage"));
const TradingBotsPage = lazy(() => import("./pages/TradingBotsPage"));
const P2PTradingPage = lazy(() => import("./pages/P2PTradingPage"));
const BotCreatorPage = lazy(() => import("./pages/BotCreatorPage"));
const KYCPage = lazy(() => import("./pages/KYCPage"));
const KYCPhonePage = lazy(() => import("./pages/KYCPhonePage"));
// Session 1 — Trading Hub + Markets + Forex pages
const TradingOverviewPage = lazy(() => import("./pages/TradingOverviewPage"));
const AllProductsPage = lazy(() => import("./pages/AllProductsPage"));
const MarketsHubPage = lazy(() => import("./pages/MarketsHubPage"));
const ForexHubPage = lazy(() => import("./pages/ForexHubPage"));
const ForexOverviewPage = lazy(() => import("./pages/ForexOverviewPage"));
const WhatIsForexPage = lazy(() => import("./pages/WhatIsForexPage"));
const HowToTradeForexPage = lazy(() => import("./pages/HowToTradeForexPage"));
const WhyTradeForexPage = lazy(() => import("./pages/WhyTradeForexPage"));
const ForexStrategiesPage = lazy(() => import("./pages/ForexStrategiesPage"));
// Session 2 — Indices + Commodities pages
const IndicesHubPage = lazy(() => import("./pages/IndicesHubPage"));
const IndicesOverviewPage = lazy(() => import("./pages/IndicesOverviewPage"));
const WhatIsIndicesPage = lazy(() => import("./pages/WhatIsIndicesPage"));
const HowToTradeIndicesPage = lazy(() => import("./pages/HowToTradeIndicesPage"));
const WhyTradeIndicesPage = lazy(() => import("./pages/WhyTradeIndicesPage"));
const IndicesStrategiesPage = lazy(() => import("./pages/IndicesStrategiesPage"));
const CommoditiesHubPage = lazy(() => import("./pages/CommoditiesHubPage"));
const CommoditiesOverviewPage = lazy(() => import("./pages/CommoditiesOverviewPage"));
const WhatIsCommoditiesPage = lazy(() => import("./pages/WhatIsCommoditiesPage"));
const HowToTradeCommoditiesPage = lazy(() => import("./pages/HowToTradeCommoditiesPage"));
const WhyTradeCommoditiesPage = lazy(() => import("./pages/WhyTradeCommoditiesPage"));
const CommoditiesStrategiesPage = lazy(() => import("./pages/CommoditiesStrategiesPage"));
// Session 3 — Shares CFD + ETFs + Bonds
const SharesHubPage = lazy(() => import("./pages/SharesHubPage"));
const SharesOverviewPage = lazy(() => import("./pages/SharesOverviewPage"));
const WhatIsSharesPage = lazy(() => import("./pages/WhatIsSharesPage"));
const HowToTradeSharesPage = lazy(() => import("./pages/HowToTradeSharesPage"));
const WhyTradeSharesPage = lazy(() => import("./pages/WhyTradeSharesPage"));
const SharesStrategiesPage = lazy(() => import("./pages/SharesStrategiesPage"));
const ETFsHubPage = lazy(() => import("./pages/ETFsHubPage"));
const ETFsOverviewPage = lazy(() => import("./pages/ETFsOverviewPage"));
const WhatIsETFsPage = lazy(() => import("./pages/WhatIsETFsPage"));
const HowToTradeETFsPage = lazy(() => import("./pages/HowToTradeETFsPage"));
const WhyTradeETFsPage = lazy(() => import("./pages/WhyTradeETFsPage"));
const ETFsStrategiesPage = lazy(() => import("./pages/ETFsStrategiesPage"));
const BondsHubPage = lazy(() => import("./pages/BondsHubPage"));
const BondsOverviewPage = lazy(() => import("./pages/BondsOverviewPage"));
const WhatIsBondsPage = lazy(() => import("./pages/WhatIsBondsPage"));
const HowToTradeBondsPage = lazy(() => import("./pages/HowToTradeBondsPage"));
const WhyTradeBondsPage = lazy(() => import("./pages/WhyTradeBondsPage"));
const BondsStrategiesPage = lazy(() => import("./pages/BondsStrategiesPage"));
// Session 4 — Accounts + Fees + Deposits/Withdrawals
const AccountsHubPage = lazy(() => import("./pages/AccountsHubPage"));
const ProECNPage = lazy(() => import("./pages/ProECNPage"));
const RawECNPage = lazy(() => import("./pages/RawECNPage"));
const StandardSTPPage = lazy(() => import("./pages/StandardSTPPage"));
const SwapFreePage = lazy(() => import("./pages/SwapFreePage"));
const SpreadsPage = lazy(() => import("./pages/SpreadsPage"));
const CommissionsPage = lazy(() => import("./pages/CommissionsPage"));
const SwapRatesPage = lazy(() => import("./pages/SwapRatesPage"));
const TradingCalculatorPage = lazy(() => import("./pages/TradingCalculatorPage"));
const DepositsPage = lazy(() => import("./pages/DepositsPage"));
const WithdrawalsPage = lazy(() => import("./pages/WithdrawalsPage"));
// Session 5 — Platforms
const PlatformsHubPage = lazy(() => import("./pages/PlatformsHubPage"));
const VarotradesAppPage = lazy(() => import("./pages/VarotradesAppPage"));
const WebTradingPage = lazy(() => import("./pages/WebTradingPage"));
const MT4Page = lazy(() => import("./pages/MT4Page"));
const MT5Page = lazy(() => import("./pages/MT5Page"));
const TradingViewPage = lazy(() => import("./pages/TradingViewPage"));
// Session 6 — Academy + Market Analysis
const TrumpTariffsPage = lazy(() => import("./pages/TrumpTariffsPage"));
const AllArticlesPage = lazy(() => import("./pages/AllArticlesPage"));
const ArticleDetailPage = lazy(() => import("./pages/ArticleDetailPage"));
const CoursesPage = lazy(() => import("./pages/CoursesPage"));
const WebinarsPage = lazy(() => import("./pages/WebinarsPage"));
const EbooksPage = lazy(() => import("./pages/EbooksPage"));
const GlossaryPage = lazy(() => import("./pages/GlossaryPage"));
const EconomicCalendarPage = lazy(() => import("./pages/EconomicCalendarPage"));
const NewsAnalysisPage = lazy(() => import("./pages/NewsAnalysisPage"));
const ClientSentimentPage = lazy(() => import("./pages/ClientSentimentPage"));
const ForexVPSPage = lazy(() => import("./pages/ForexVPSPage"));
const TradingSignalsPage = lazy(() => import("./pages/TradingSignalsPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
// Session 7 — Partners + Promotions
const PartnersHubPage = lazy(() => import("./pages/PartnersHubPage"));
const AffiliateProgramPage = lazy(() => import("./pages/AffiliateProgramPage"));
const IBPage = lazy(() => import("./pages/IBPage"));
const MoneyManagerPage = lazy(() => import("./pages/MoneyManagerPage"));
const PromotionsHubPage = lazy(() => import("./pages/PromotionsHubPage"));
const WelcomeBonusPage = lazy(() => import("./pages/WelcomeBonusPage"));
const TradingContestsPage = lazy(() => import("./pages/TradingContestsPage"));
// Session 8 — About + Sitemap
const AboutHubPage = lazy(() => import("./pages/AboutHubPage"));
const WhyVaroTradesPage = lazy(() => import("./pages/WhyVaroTradesPage"));
const AwardsPage = lazy(() => import("./pages/AwardsPage"));
const RegulationPage = lazy(() => import("./pages/RegulationPage"));
const SustainabilityPage = lazy(() => import("./pages/SustainabilityPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const LegalDocumentsPage = lazy(() => import("./pages/LegalDocumentsPage"));
const SitemapPage = lazy(() => import("./pages/SitemapPage"));
// Session 9 — About sub-pages (Media, Insurance, ESG, Press, Servers)
const MediaCentrePage = lazy(() => import("./pages/MediaCentrePage"));
const FundInsurancePage = lazy(() => import("./pages/FundInsurancePage"));
const ESGPage = lazy(() => import("./pages/ESGPage"));
const PressReleasesPage = lazy(() => import("./pages/PressReleasesPage"));
const TradingServersPage = lazy(() => import("./pages/TradingServersPage"));
const ClientDashboard = lazy(() => import("./pages/ClientDashboard"));
const DashboardDeposit = lazy(() => import("./pages/DashboardDeposit"));
const RewardsPage = lazy(() => import("./pages/RewardsPage"));
const AccountsPage = lazy(() => import("./pages/AccountsPage"));
const FundsPage = lazy(() => import("./pages/FundsPage"));
const VWalletPage = lazy(() => import("./pages/VWalletPage"));
const MobileVerifyPage = lazy(() => import("./pages/MobileVerifyPage"));
const AdminKYCPage = lazy(() => import("./pages/AdminKYCPage"));
const SuperAdminPanel = lazy(() => import("./pages/SuperAdminPanel"));
const TenantCRM = lazy(() => import("./pages/TenantCRM"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const UnifiedAdmin = lazy(() => import("./pages/UnifiedAdmin"));
const TenantLoginPage = lazy(() => import("./pages/TenantLoginPage"));
const ProviderDetailPage = lazy(() => import("./pages/ProviderDetailPage"));
const CopierDashboardPage = lazy(() => import("./pages/CopierDashboardPage"));
const BecomeProviderPage = lazy(() => import("./pages/BecomeProviderPage"));
const InstallAppPage = lazy(() => import("./pages/InstallAppPage"));

// Animated VAROTRADES logo page loader
const PageLoader = () => (
  <div className="fixed top-0 inset-x-0 z-[9998] pointer-events-none" data-testid="page-loader" aria-hidden="true">
    {/* Top progress bar — non-blocking, content underneath stays interactive */}
    <div className="h-[3px] w-full bg-transparent overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-transparent via-[#D85308] to-transparent animate-[vt-progress_1.1s_cubic-bezier(0.4,0,0.2,1)_infinite]"
        style={{ width: '40%' }}
      />
    </div>
    {/* Subtle bottom-right spinner so user knows something is loading */}
    <div className="fixed bottom-4 right-4 w-9 h-9 rounded-full bg-white shadow-[0_4px_18px_rgba(20,15,10,0.18)] border border-ink-100 grid place-items-center pointer-events-none">
      <div className="w-4 h-4 rounded-full border-[2px] border-[#D85308]/25 border-t-[#D85308] animate-spin" />
    </div>
  </div>
);


const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAuthenticated, isAdmin } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route - redirect based on role
const PublicRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    if (['super_admin', 'tenant_admin', 'tenant_agent', 'admin'].includes(user?.role)) return <Navigate to="/admin/panel" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <SSOHandler />
      <ScrollToTop />
      <ActivityTracker />
      <GlobalModalScrollLock />
      <SplashController />
      <VerifyProvider>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/app" element={<InstallAppPage />} />
        <Route path="/install" element={<InstallAppPage />} />
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
        <Route path="/m/verify/:token" element={<MobileVerifyPage />} />
        <Route path="/markets" element={<MarketsPage />} />
        {/* Session 1 — Trading + Markets + Forex */}
        <Route path="/trading" element={<TradingOverviewPage />} />
        <Route path="/trading/products" element={<AllProductsPage />} />
        <Route path="/markets-hub" element={<MarketsHubPage />} />
        <Route path="/markets/forex" element={<ForexHubPage />} />
        <Route path="/markets/forex/overview" element={<ForexOverviewPage />} />
        <Route path="/markets/forex/what-is" element={<WhatIsForexPage />} />
        <Route path="/markets/forex/how-to" element={<HowToTradeForexPage />} />
        <Route path="/markets/forex/why" element={<WhyTradeForexPage />} />
        <Route path="/markets/forex/strategies" element={<ForexStrategiesPage />} />
        {/* Session 2 — Indices */}
        <Route path="/markets/indices" element={<IndicesHubPage />} />
        <Route path="/markets/indices/overview" element={<IndicesOverviewPage />} />
        <Route path="/markets/indices/what-is" element={<WhatIsIndicesPage />} />
        <Route path="/markets/indices/how-to" element={<HowToTradeIndicesPage />} />
        <Route path="/markets/indices/why" element={<WhyTradeIndicesPage />} />
        <Route path="/markets/indices/strategies" element={<IndicesStrategiesPage />} />
        {/* Session 2 — Commodities */}
        <Route path="/markets/commodities" element={<CommoditiesHubPage />} />
        <Route path="/markets/commodities/overview" element={<CommoditiesOverviewPage />} />
        <Route path="/markets/commodities/what-is" element={<WhatIsCommoditiesPage />} />
        <Route path="/markets/commodities/how-to" element={<HowToTradeCommoditiesPage />} />
        <Route path="/markets/commodities/why" element={<WhyTradeCommoditiesPage />} />
        <Route path="/markets/commodities/strategies" element={<CommoditiesStrategiesPage />} />
        {/* Session 3 — Shares CFD */}
        <Route path="/markets/shares" element={<SharesHubPage />} />
        <Route path="/markets/shares/overview" element={<SharesOverviewPage />} />
        <Route path="/markets/shares/what-is" element={<WhatIsSharesPage />} />
        <Route path="/markets/shares/how-to" element={<HowToTradeSharesPage />} />
        <Route path="/markets/shares/why" element={<WhyTradeSharesPage />} />
        <Route path="/markets/shares/strategies" element={<SharesStrategiesPage />} />
        {/* Session 3 — ETFs */}
        <Route path="/markets/etfs" element={<ETFsHubPage />} />
        <Route path="/markets/etfs/overview" element={<ETFsOverviewPage />} />
        <Route path="/markets/etfs/what-is" element={<WhatIsETFsPage />} />
        <Route path="/markets/etfs/how-to" element={<HowToTradeETFsPage />} />
        <Route path="/markets/etfs/why" element={<WhyTradeETFsPage />} />
        <Route path="/markets/etfs/strategies" element={<ETFsStrategiesPage />} />
        {/* Session 3 — Bonds */}
        <Route path="/markets/bonds" element={<BondsHubPage />} />
        <Route path="/markets/bonds/overview" element={<BondsOverviewPage />} />
        <Route path="/markets/bonds/what-is" element={<WhatIsBondsPage />} />
        <Route path="/markets/bonds/how-to" element={<HowToTradeBondsPage />} />
        <Route path="/markets/bonds/why" element={<WhyTradeBondsPage />} />
        <Route path="/markets/bonds/strategies" element={<BondsStrategiesPage />} />
        {/* Session 4 — Accounts */}
        <Route path="/accounts" element={<AccountsHubPage />} />
        <Route path="/accounts/pro-ecn" element={<ProECNPage />} />
        <Route path="/accounts/raw-ecn" element={<RawECNPage />} />
        <Route path="/accounts/standard" element={<StandardSTPPage />} />
        <Route path="/accounts/swap-free" element={<SwapFreePage />} />
        {/* Session 4 — Fees */}
        <Route path="/fees/spreads" element={<SpreadsPage />} />
        <Route path="/fees/commissions" element={<CommissionsPage />} />
        <Route path="/fees/swap-rates" element={<SwapRatesPage />} />
        <Route path="/fees/calculator" element={<TradingCalculatorPage />} />
        {/* Session 4 — Funds */}
        <Route path="/funds/deposit" element={<DepositsPage />} />
        <Route path="/funds/withdrawal" element={<WithdrawalsPage />} />
        {/* Session 5 — Platforms */}
        <Route path="/platforms" element={<PlatformsHubPage />} />
        <Route path="/platforms/app" element={<VarotradesAppPage />} />
        <Route path="/platforms/web" element={<WebTradingPage />} />
        <Route path="/platforms/mt4" element={<MT4Page />} />
        <Route path="/platforms/mt5" element={<MT5Page />} />
        <Route path="/platforms/tradingview" element={<TradingViewPage />} />
        {/* Session 6 — Academy */}
        <Route path="/learn/trump-tariffs" element={<TrumpTariffsPage />} />
        <Route path="/learn/articles" element={<AllArticlesPage />} />
        <Route path="/learn/articles/:slug" element={<ArticleDetailPage />} />
        <Route path="/learn/courses" element={<Navigate to="/learn/ebooks" replace />} />
        <Route path="/learn/webinars" element={<Navigate to="/learn/ebooks" replace />} />
        <Route path="/learn/ebooks" element={<EbooksPage />} />
        <Route path="/learn/glossary" element={<GlossaryPage />} />
        {/* Session 6 — Market Analysis */}
        <Route path="/analysis/calendar" element={<EconomicCalendarPage />} />
        <Route path="/analysis/news" element={<NewsAnalysisPage />} />
        <Route path="/analysis/sentiment" element={<ClientSentimentPage />} />
        <Route path="/analysis/vps" element={<ForexVPSPage />} />
        <Route path="/analysis/signals" element={<TradingSignalsPage />} />
        <Route path="/analysis/notifications" element={<NotificationsPage />} />
        {/* Session 7 — Partners */}
        <Route path="/partners" element={<PartnersHubPage />} />
        <Route path="/partners/affiliate" element={<AffiliateProgramPage />} />
        <Route path="/partners/cpa" element={<AffiliateProgramPage />} />
        <Route path="/partners/ib" element={<IBPage />} />
        <Route path="/partners/money-manager" element={<MoneyManagerPage />} />
        {/* Session 7 — Promotions */}
        <Route path="/promotions" element={<PromotionsHubPage />} />
        <Route path="/promotions/welcome-bonus" element={<WelcomeBonusPage />} />
        <Route path="/promotions/deposit-bonus" element={<WelcomeBonusPage />} />
        <Route path="/promotions/contests" element={<TradingContestsPage />} />
        {/* Session 8 — About */}
        <Route path="/about" element={<AboutHubPage />} />
        <Route path="/about/why" element={<WhyVaroTradesPage />} />
        <Route path="/about/awards" element={<AwardsPage />} />
        <Route path="/about/regulation" element={<RegulationPage />} />
        <Route path="/about/sustainability" element={<SustainabilityPage />} />
        <Route path="/about/contact" element={<ContactPage />} />
        <Route path="/about/legal" element={<LegalDocumentsPage />} />
        {/* Session 9 — About sub-pages */}
        <Route path="/about/media" element={<MediaCentrePage />} />
        <Route path="/about/insurance" element={<FundInsurancePage />} />
        <Route path="/about/esg" element={<ESGPage />} />
        <Route path="/about/press" element={<PressReleasesPage />} />
        <Route path="/about/servers" element={<TradingServersPage />} />
        <Route path="/sitemap" element={<SitemapPage />} />
        <Route path="/learn" element={<LearnPage />} />
        <Route path="/company" element={<CompanyPage />} />
        <Route path="/legal/:type" element={<LegalPage />} />
        <Route path="/fees" element={<FeesPage />} />
        <Route path="/spot-trading" element={<SpotTradingPage />} />
        <Route path="/futures" element={<FuturesPage />} />
        <Route path="/margin-trading" element={<MarginTradingPage />} />
        <Route path="/copy-trading" element={<CopyTradingPage />} />
        <Route path="/copy-trading/dashboard" element={<ProtectedRoute><CopierDashboardPage /></ProtectedRoute>} />
        <Route path="/copy-trading/become-provider" element={<ProtectedRoute><BecomeProviderPage /></ProtectedRoute>} />
        <Route path="/copy-trading/provider/:providerId" element={<ProviderDetailPage />} />
        <Route path="/trading-bots" element={<TradingBotsPage />} />
        <Route path="/p2p-trading" element={<P2PTradingPage />} />
        <Route path="/create-bot" element={<BotCreatorPage />} />
        <Route path="/kyc" element={<ProtectedRoute><KYCPage /></ProtectedRoute>} />
        <Route path="/kyc-phone/:token" element={<KYCPhonePage />} />
        
        {/* Protected Routes */}
        <Route path="/dashboard" element={<ProtectedRoute><ClientDashboard /></ProtectedRoute>} />
        <Route path="/dashboard/deposit" element={<ProtectedRoute><DashboardDeposit /></ProtectedRoute>} />
        <Route path="/dashboard/rewards" element={<ProtectedRoute><RewardsPage /></ProtectedRoute>} />
        <Route path="/my-accounts" element={<ProtectedRoute><AccountsPage /></ProtectedRoute>} />
        <Route path="/funds" element={<ProtectedRoute><FundsPage /></ProtectedRoute>} />
        <Route path="/v-wallet" element={<ProtectedRoute><VWalletPage /></ProtectedRoute>} />
        <Route path="/platform" element={<ExternalTradeRedirect />} />
        <Route path="/trade" element={<ExternalTradeRedirect />} />
        <Route path="/status" element={<StatusPage />} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        
        {/* Admin Portal - unified entry point */}
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/panel" element={<ProtectedRoute><UnifiedAdmin /></ProtectedRoute>} />
        <Route path="/admin/kyc" element={<ProtectedRoute adminOnly><AdminKYCPage /></ProtectedRoute>} />
        
        {/* Legacy routes redirect to unified admin */}
        <Route path="/super-admin" element={<Navigate to="/admin/panel" replace />} />
        <Route path="/crm" element={<Navigate to="/admin/panel" replace />} />
        
        {/* Tenant White-Label Login */}
        <Route path="/t/:slug" element={<TenantLoginPage />} />
        
        {/* Redirect unknown routes */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <GlobalAIChat />
      <RouteScrollGuard />
      </VerifyProvider>
    </Suspense>
  );
}

/**
 * Renders the AI Chat Widget globally on every page, except routes where it
 * would be intrusive (admin panel, auth flows, mobile-verify, trading platform
 * & client portal which has its own dashboard chrome).
 */
const GlobalAIChat = () => {
  const { pathname } = useLocation();
  const HIDDEN_PREFIXES = ['/admin', '/login', '/register', '/m/verify', '/dashboard', '/portal', '/trade', '/kyc'];
  if (HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))) return null;
  return <AIChatWidget />;
};

function App() {
  return (
    <div className="App overflow-x-hidden w-full max-w-full">
      <JsonLd schema={organizationSchema()} id="vt-org" />
      <JsonLd schema={websiteSchema()} id="vt-website" />
      <JsonLd schema={financialServiceSchema()} id="vt-fs" />
      <BrowserRouter>
        <Analytics />
        <LanguageProvider>
          <AuthProvider>
            <SmoothScroll>
              <AppRoutes />
              <PromoPopup />
              <ExitIntentPopup />
              <CookieConsentBanner />
              <SonnerToaster
                position="top-right"
                richColors
                closeButton
                duration={5000}
                toastOptions={{
                  style: { fontFamily: 'inherit' },
                }}
              />
            </SmoothScroll>
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
